import { Link } from "react-router-dom";
import { Send, MessageCircle, Phone, ShieldCheck } from "lucide-react";
import { BRAND, LINKS } from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";
import PaymentStrip from "@/components/PaymentStrip";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer
      data-testid="footer"
      className="relative mt-20 border-t border-orange-500/20 bg-[#06060a]"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      {/* Payments */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 pb-8">
        <PaymentStrip title={t.footer.payments} sub={t.footer.paymentsSub} />
      </div>

      <div className="relative divider-thread mx-4 sm:mx-6 lg:mx-10" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="U2 Gaming" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-display text-3xl text-orange-grad tracking-wider">
                {BRAND.name}
              </div>
              <div className="text-[11px] tracking-[0.3em] text-orange-300/70 uppercase">
                {t.brand.tag2}
              </div>
            </div>
          </div>
          <p className="mt-5 text-sm text-zinc-400 max-w-md leading-relaxed">
            {t.footer.brandDesc}
          </p>
          <div className="mt-5 flex items-center gap-2 text-xs text-orange-200/70">
            <ShieldCheck className="w-4 h-4" /> {t.footer.security}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="font-display text-xl tracking-wider text-orange-300">
            {t.footer.quickLinks}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/" className="text-zinc-400 hover:text-orange-300">{t.nav.home}</Link></li>
            <li><Link to="/game" className="text-zinc-400 hover:text-orange-300">{t.nav.games}</Link></li>
            <li><Link to="/contact" className="text-zinc-400 hover:text-orange-300">{t.nav.contact}</Link></li>
            <li>
              <a
                href={LINKS.login}
                target="_blank"
                rel="noreferrer"
                className="text-zinc-400 hover:text-orange-300"
              >
                {t.nav.login}
              </a>
            </li>
            <li>
              <a
                href={LINKS.register}
                target="_blank"
                rel="noreferrer"
                className="text-zinc-400 hover:text-orange-300"
              >
                {t.nav.register}
              </a>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-display text-xl tracking-wider text-orange-300">
            {t.footer.reachUs}
          </h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a
                href={LINKS.telegram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-telegram"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-orange-300"
              >
                <Send className="w-4 h-4 text-sky-400" /> {t.footer.tgGroup}
              </a>
            </li>
            <li>
              <a
                href={LINKS.whatsappLink}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-orange-300"
              >
                <MessageCircle className="w-4 h-4 text-emerald-400" /> {t.footer.waChat}
              </a>
            </li>
            <li className="inline-flex items-center gap-2 text-zinc-300">
              <Phone className="w-4 h-4 text-orange-400" /> {LINKS.whatsappNumber}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-orange-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} {BRAND.name}. {t.footer.copyright}</p>
          <p className="text-orange-200/60 tracking-wider uppercase">
            {t.footer.responsible}
          </p>
        </div>
      </div>
    </footer>
  );
}
